import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Business,
  Security,
  QrCode,
  ArrowBack,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import axiosInstance from '../services/axiosConfig';
import { useSearchParams } from 'react-router-dom';

type LoginStep = 'email' | 'password' | 'sso';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, completeLogin, migratePassword } = useAuth();
  const [loginStep, setLoginStep] = useState<LoginStep>('email');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [ssoProvider, setSsoProvider] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [requiresPasswordUpdate, setRequiresPasswordUpdate] = useState(false);
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [setupData, setSetupData] = useState<{ qrCode: string; manualEntryKey: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [resetToken, setResetToken] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [passwordMigrationCompleted, setPasswordMigrationCompleted] = useState(false);
  const [completionTokenState, setCompletionTokenState] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for SSO error in URL params
  useEffect(() => {
    const ssoError = searchParams.get('error');
    if (ssoError) {
      setError(ssoError);
    }
  }, [searchParams]);

  useEffect(() => {
    // Auto-start 2FA setup when required
    if (requiresSetup && !setupData) {
      handleSetupStart();
    }
  }, [requiresSetup]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  // Step 1: Check email — determines SSO or password
  const handleEmailCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) return;
    try {
      setError('');
      setLoading(true);
      const response = await axiosInstance.post('/auth/check-email', { email: formData.email });
      const { method, provider, ssoUrlPartner, ssoUrl: mainSsoUrl } = response.data;

      if (method === 'sso' && (ssoUrlPartner || mainSsoUrl)) {
        setSsoProvider(provider);
        // Use partner-specific SSO URL and pass our origin so backend redirects back here
        const partnerUrl = ssoUrlPartner || `${mainSsoUrl}&portal=partner`;
        setSsoUrl(`${partnerUrl}&portalOrigin=${encodeURIComponent(window.location.origin)}`);
        setLoginStep('sso');
      } else {
        setLoginStep('password');
      }
    } catch (err: any) {
      // On error, fall back to password login
      setLoginStep('password');
    } finally {
      setLoading(false);
    }
  };

  // SSO redirect
  const handleSSORedirect = () => {
    window.location.href = ssoUrl;
  };

  // Go back to email step
  const handleBackToEmail = () => {
    setLoginStep('email');
    setFormData(prev => ({ ...prev, password: '' }));
    setSsoProvider('');
    setSsoUrl('');
    setError('');
    setRequires2FA(false);
    setRequiresPasswordUpdate(false);
  };

  const handleSetupStart = async () => {
    try {
      setError('');
      const response = await axiosInstance.post('/auth/2fa/setup');
      setSetupData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to setup 2FA');
    }
  };

  const handleSetupComplete = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const response = await axiosInstance.post('/auth/2fa/verify', { totpCode });

      setBackupCodes(response.data.backupCodes);

      // Update authentication with new token
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.removeItem('tempToken');

        // Redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000); // Give user time to see backup codes
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to verify 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordMigration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const result = await migratePassword(resetToken, newPassword);

      if (result?.passwordUpdated) {
        setPasswordMigrationCompleted(true);
        setRequiresPasswordUpdate(false);
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');

        if (result.completionToken) {
          setCompletionTokenState(result.completionToken);
        }

        if (result.requiresSetup) {
          // Handle 2FA setup requirement - need to complete login to get tempToken
          console.log('[Partner Portal] Password updated, 2FA setup required');
          const loginResult = await completeLogin(result.completionToken || completionTokenState);

          if (loginResult?.requiresSetup && loginResult.tempToken) {
            console.log('[Partner Portal] Got tempToken for 2FA setup');
            setRequiresSetup(true);
            setTempToken(loginResult.tempToken);
            // Set tempToken in localStorage and axios header
            localStorage.setItem('token', loginResult.tempToken);
            localStorage.setItem('tempToken', loginResult.tempToken);
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${loginResult.tempToken}`;
          }
        } else if (result.requires2FA) {
          setRequires2FA(true);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);

      let result;

      if (passwordMigrationCompleted && completionTokenState) {
        // Use complete login flow after password migration (uses signed token)
        console.log('[Partner Portal] Completing login after password migration');
        result = await completeLogin(completionTokenState, totpCode || undefined);
      } else {
        // Normal login flow
        console.log('[Partner Portal] Login attempt with:', { email: formData.email, has2FA: !!totpCode });
        result = await login(formData.email, formData.password, totpCode || undefined);
      }

      console.log('[Partner Portal] Login result:', result);

      if (result?.requires2FA) {
        console.log('[Partner Portal] 2FA required');
        setRequires2FA(true);
        setError('');
      } else if (result?.requiresPasswordUpdate) {
        console.log('[Partner Portal] Password update required');
        setRequiresPasswordUpdate(true);
        setResetToken(result.resetToken);
        setError('');
      } else if (result?.requiresSetup) {
        console.log('[Partner Portal] 2FA setup required');
        setRequiresSetup(true);
        setTempToken(result.tempToken);
        // Set tempToken in localStorage and axios header
        if (result.tempToken) {
          localStorage.setItem('token', result.tempToken);
          localStorage.setItem('tempToken', result.tempToken);
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${result.tempToken}`;
        }
      } else {
        // Successfully logged in - stay in partner portal
        console.log('[Partner Portal] Login successful, navigating to dashboard');
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('[Partner Portal] Login error:', err);

      if (err.response?.data?.requires2FA) {
        setRequires2FA(true);
        setError('');
      } else {
        setError(err.response?.data?.message || err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Logo/Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Partner Portal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to access your partner dashboard
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* 2FA Setup */}
            {requiresSetup && setupData && !backupCodes.length && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Security Setup Required:</strong> Please scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)
                  </Typography>
                </Alert>

                <Box display="flex" justifyContent="center" mb={3}>
                  <Box p={2} bgcolor="white" borderRadius={1}>
                    <img
                      src={setupData.qrCode}
                      alt="2FA QR Code"
                      style={{ width: 200, height: 200 }}
                    />
                  </Box>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    Manual entry key: {setupData.manualEntryKey}
                  </Typography>
                </Alert>

                <TextField
                  fullWidth
                  label="Enter 6-digit code from your authenticator app"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  inputProps={{ maxLength: 6 }}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Security color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSetupComplete}
                  disabled={loading || totpCode.length !== 6}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b4592 100%)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Enable 2FA'}
                </Button>
              </Box>
            )}

            {/* Backup Codes Display */}
            {backupCodes.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    2FA has been enabled successfully! Please save these backup codes in a secure location.
                    You can use them to access your account if you lose your authenticator device.
                  </Typography>
                </Alert>

                <Typography variant="h6" gutterBottom>
                  Backup Codes
                </Typography>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {backupCodes.map((code, index) => (
                    <Grid item xs={6} key={index}>
                      <Box
                        p={1}
                        bgcolor="grey.100"
                        borderRadius={1}
                        textAlign="center"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {code}
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Store these codes in a safe place. Each code can only be used once.
                  </Typography>
                </Alert>

                <Typography variant="caption" color="text.secondary" align="center" display="block">
                  Redirecting to dashboard in 3 seconds...
                </Typography>
              </Box>
            )}

            {/* Login Form — Step-based: email → SSO or password */}
            {!requiresSetup && !backupCodes.length && (
              <>
                {/* Step 1: Email */}
                {loginStep === 'email' && (
                  <form onSubmit={handleEmailCheck}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      margin="normal"
                      disabled={loading}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 3 }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading || !formData.email}
                      sx={{
                        py: 1.5,
                        mb: 2,
                        background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b4592 100%)' },
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
                    </Button>
                  </form>
                )}

                {/* Step SSO: Redirect to IdP */}
                {loginStep === 'sso' && (
                  <Box>
                    <Button size="small" startIcon={<ArrowBack />} onClick={handleBackToEmail} sx={{ mb: 2 }}>
                      Back
                    </Button>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        Your organization uses <strong>{ssoProvider === 'google' ? 'Google' : ssoProvider === 'microsoft' ? 'Microsoft' : 'SSO'}</strong> for authentication.
                      </Typography>
                    </Alert>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Signing in as <strong>{formData.email}</strong>
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleSSORedirect}
                      sx={{
                        py: 1.5,
                        mb: 2,
                        background: ssoProvider === 'google'
                          ? 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)'
                          : ssoProvider === 'microsoft'
                          ? 'linear-gradient(135deg, #00a4ef 0%, #7fba00 100%)'
                          : 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
                        '&:hover': { opacity: 0.9 },
                      }}
                    >
                      {ssoProvider === 'google' ? 'Sign in with Google' : ssoProvider === 'microsoft' ? 'Sign in with Microsoft' : 'Sign in with SSO'}
                    </Button>
                  </Box>
                )}

                {/* Step Password: Normal login */}
                {loginStep === 'password' && (
                  <form onSubmit={requiresPasswordUpdate ? handlePasswordMigration : handleSubmit}>
                    <Button size="small" startIcon={<ArrowBack />} onClick={handleBackToEmail} sx={{ mb: 2 }}>
                      Back
                    </Button>

                    {requiresPasswordUpdate && (
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        Your password needs to be updated to meet new security requirements.
                        Password must be at least 12 characters with uppercase, lowercase, numbers, and special characters.
                      </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Signing in as <strong>{formData.email}</strong>
                    </Typography>

                    {!requiresPasswordUpdate && (
                      <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        required
                        margin="normal"
                        disabled={loading || requires2FA || passwordMigrationCompleted}
                        autoFocus
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: requires2FA ? 2 : 3 }}
                      />
                    )}

                    {requiresPasswordUpdate && (
                      <>
                        <TextField
                          fullWidth label="New Password" name="newPassword"
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                          required margin="normal" disabled={loading} autoFocus
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth label="Confirm New Password" name="confirmPassword"
                          type={showNewPassword ? 'text' : 'password'}
                          value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                          required margin="normal" disabled={loading}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment> }}
                          sx={{ mb: 3 }}
                        />
                      </>
                    )}

                    {requires2FA && (
                      <TextField
                        fullWidth label="2FA Code" name="totpCode" type="text"
                        value={totpCode} onChange={(e) => setTotpCode(e.target.value)}
                        required margin="normal" disabled={loading} autoFocus
                        placeholder="Enter 6-digit code from your authenticator app"
                        inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Security color="action" /></InputAdornment> }}
                        sx={{ mb: 3 }}
                      />
                    )}

                    {passwordMigrationCompleted && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Password updated successfully! Please complete login with 2FA if enabled.
                      </Alert>
                    )}

                    <Button
                      type="submit" fullWidth variant="contained" size="large" disabled={loading}
                      sx={{
                        py: 1.5, mb: 2,
                        background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b4592 100%)' },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : requiresPasswordUpdate ? (
                        'Update Password & Continue'
                      ) : requires2FA ? (
                        'Verify 2FA Code'
                      ) : (
                        'Sign In'
                      )}
                    </Button>

                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Link to="/forgot-password" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.875rem' }}>
                        Forgot Password?
                      </Link>
                    </Box>
                  </form>
                )}

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">OR</Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Not a partner yet?{' '}
                    <Link to="/" style={{ color: '#667eea', textDecoration: 'none', fontWeight: 'bold' }}>
                      Learn More
                    </Link>
                  </Typography>
                </Box>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" align="center" display="block">
                © 2025 Tripl-i. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LoginPage;
